import React, { useCallback, useEffect, useMemo, useState } from "react";
import DraggablePanel from "./DraggablePanel";
import BehaviorHeaderBar from "../behavior-editor/BehaviorHeaderBar";
import BehaviorTreeCanvas from "../behavior-editor/BehaviorTreeCanvas";
import {
  useBehaviorActions,
  useSelectedBehaviorId,
  useBehaviorsMap,
  useBehaviorRunState,
  useNodeStatuses,
} from "../../stores/behaviorStore";
import { usePhysicsStore } from "../../stores/physicsStore";
import { useBodies, useSites } from "../../stores/sceneStore";
import { logger } from "../../utils/logger";
import { resolveEndEffectorBodyUuid } from "../../behavior/endEffectorResolve";
import {
  getBehaviorTemplateAvailabilityForContext,
  getDefaultSelectedBehaviorId,
} from "../../behavior/context/rulesRegistry";
import { startSimulationFromCurrentFiles, stopSimulation } from "../../physics/mujocoBridge";
import panelConfig from "../../utils/panelConfig";

const { getStandardPanelProps, getStandardPosition } = panelConfig;

function waitForPhysicsRunning({ timeoutMs = 15000, pollMs = 50 } = {}) {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();

    const check = () => {
      const state = usePhysicsStore.getState();
      if (state.isRunning) {
        resolve();
        return;
      }
      if (state.status === "error") {
        reject(new Error(state.lastError || "Physics failed to initialize"));
        return;
      }
      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error("Timed out waiting for physics to initialize"));
        return;
      }
      window.setTimeout(check, pollMs);
    };

    check();
  });
}

function EmptyCanvas({ hasBehaviors, onNew }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center p-8">
      <div className="text-gray-400 text-sm font-medium mb-1">
        {hasBehaviors ? "No behavior selected" : "No behaviors yet"}
      </div>
      <div className="panel-text-helper text-xs mb-4 max-w-xs">
        {hasBehaviors
          ? "Choose a behavior from the Behaviors menu in the toolbar above, or create a new one."
          : "Behaviors are tick-driven plans the robot follows. Start from a built-in template or create a new one."}
      </div>
      <button
        type="button"
        onClick={onNew}
        className="inline-flex items-center gap-1.5 rounded-md bg-sky-500/90 hover:bg-sky-500 text-white text-xs font-medium px-3 py-1.5 transition-colors"
      >
        New behavior
      </button>
    </div>
  );
}

export default function BehaviorEditorPanel({ open, onClose, titleIcon, mobilePinPanelId }) {
  const behaviors = useBehaviorsMap();
  const selectedId = useSelectedBehaviorId();
  const actions = useBehaviorActions();
  const { isRunning, lastTickMessage, lastEpisodeOutcome, activeNodeId } = useBehaviorRunState();
  const nodeStatuses = useNodeStatuses();
  const physicsRunning = usePhysicsStore((s) => s.isRunning);
  const bodies = useBodies();
  const sites = useSites();
  const [isStartingPhysics, setIsStartingPhysics] = useState(false);
  const [runValidationIssues, setRunValidationIssues] = useState([]);
  const [importError, setImportError] = useState("");
  const prevOpenRef = React.useRef(false);

  const doc = selectedId ? behaviors[selectedId] : null;

  const behaviorList = useMemo(
    () =>
      Object.values(behaviors).sort((a, b) => {
        const at = a.isTemplate ? 0 : 1;
        const bt = b.isTemplate ? 0 : 1;
        if (at !== bt) return at - bt;
        return a.name.localeCompare(b.name);
      }),
    [behaviors]
  );
  const { unavailableTemplateIds, userHint } = useMemo(() => {
    void bodies;
    return getBehaviorTemplateAvailabilityForContext(behaviorList);
  }, [behaviorList, bodies]);

  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (!open || wasOpen) return;
    const nextId = getDefaultSelectedBehaviorId(behaviors);
    if (!nextId) return;
    if (!selectedId || unavailableTemplateIds.has(selectedId)) {
      if (nextId !== selectedId) actions.selectBehavior(nextId);
    }
  }, [open, behaviors, selectedId, unavailableTemplateIds, actions]);

  const activeNodeLabel = useMemo(() => {
    if (!doc || !activeNodeId) return null;
    return doc.nodes[activeNodeId]?.kind ?? null;
  }, [doc, activeNodeId]);

  useEffect(() => {
    if (!isRunning) return;
    const id = window.setInterval(() => {
      actions.tickRunner();
    }, 20);
    return () => window.clearInterval(id);
  }, [isRunning, actions]);

  const handleExport = useCallback(() => {
    if (!selectedId || !doc) return;
    const json = actions.exportBehaviorJson(selectedId);
    if (!json) return;
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeName = (doc.name || "behavior").replace(/[^\w.-]+/g, "_");
    a.href = url;
    a.download = `${safeName}.behavior.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [actions, doc, selectedId]);

  const handleImport = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json,.json";
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const text = typeof reader.result === "string" ? reader.result : "";
        const result = actions.importBehaviorJson(text);
        if (result?.ok) {
          setImportError("");
          return;
        }
        const message =
          result?.reason === "parse"
            ? "File could not be parsed as JSON."
            : "Not a valid behavior file";
        setImportError(message);
      };
      reader.onerror = () => {
        setImportError("File could not be parsed as JSON.");
      };
      reader.readAsText(file);
    };
    input.click();
  }, [actions]);

  const handleRun = useCallback(async () => {
    if (!selectedId || isRunning || isStartingPhysics) return;
    const issues = [];
    const bodyExists = (uuid) => Boolean(uuid && bodies?.[uuid]);
    const nodeLabel = (node) => node.label?.trim() || node.kind;
    Object.values(doc?.nodes || {}).forEach((node) => {
      if (!node || typeof node !== "object") return;
      const label = nodeLabel(node);
      if (node.kind === "servoPickPlace") {
        if (!bodyExists(node.pickBodyUuid)) issues.push(`${label}: pick body is missing`);
        if (node.dropBodyUuid && !bodyExists(node.dropBodyUuid))
          issues.push(`${label}: drop body is missing`);
        if (!resolveEndEffectorBodyUuid({ bodies, sites }, node.endEffectorRef || "")) {
          issues.push(`${label}: end effector is missing or disconnected`);
        }
      } else if (
        node.kind === "moveEeToXyz" ||
        node.kind === "moveEeByOffset" ||
        node.kind === "conditionEeNear"
      ) {
        if (!resolveEndEffectorBodyUuid({ bodies, sites }, node.endEffectorRef || "")) {
          issues.push(`${label}: end effector is missing or disconnected`);
        }
        if (
          node.kind === "conditionEeNear" &&
          node.targetBodyUuid &&
          !bodyExists(node.targetBodyUuid)
        ) {
          issues.push(`${label}: target body is missing`);
        }
      } else if (
        node.kind === "randomizeObjectPose" ||
        node.kind === "setObjectPose" ||
        node.kind === "conditionObjectGrasped"
      ) {
        if (!bodyExists(node.bodyUuid)) issues.push(`${label}: body is missing`);
      } else if (node.kind === "conditionObjectNear") {
        if (!bodyExists(node.bodyUuid)) issues.push(`${label}: body is missing`);
        if (node.targetBodyUuid && !bodyExists(node.targetBodyUuid))
          issues.push(`${label}: target body is missing`);
      }
    });
    if (issues.length > 0) {
      setRunValidationIssues(issues);
      return;
    }
    setRunValidationIssues([]);
    try {
      if (!physicsRunning) {
        setIsStartingPhysics(true);
        try {
          await startSimulationFromCurrentFiles();
          await waitForPhysicsRunning();
        } finally {
          setIsStartingPhysics(false);
        }
      }
      actions.startRun(selectedId);
    } catch (e) {
      logger.error("[BehaviorEditor] Run: failed to start physics or behavior", e);
      setRunValidationIssues([`Run failed: ${e?.message || String(e)}`]);
    }
  }, [actions, bodies, doc, isRunning, isStartingPhysics, physicsRunning, selectedId, sites]);

  const handleStop = useCallback(async () => {
    actions.stopRun();
    if (!usePhysicsStore.getState().isRunning) return;
    try {
      await stopSimulation();
      setRunValidationIssues([]);
    } catch (e) {
      logger.error("[BehaviorEditor] Stop: failed to stop physics", e);
      setRunValidationIssues([`Stop failed: ${e?.message || String(e)}`]);
    }
  }, [actions]);

  if (!open) return null;

  return (
    <DraggablePanel
      title={
        <span className="text-sm font-semibold text-gray-100 select-none leading-tight">
          Behavior Editor
        </span>
      }
      titleIcon={titleIcon}
      onClose={onClose}
      {...getStandardPanelProps({
        initialSize: { width: 980, height: 660 },
        minWidth: 720,
        minHeight: 440,
      })}
      initialPosition={getStandardPosition({ x: 360, y: 10 })}
      mobilePinPanelId={mobilePinPanelId}
    >
      <div className="flex flex-col h-full text-gray-200 rounded-lg overflow-hidden">
        <BehaviorHeaderBar
          behaviorList={behaviorList}
          unavailableTemplateIds={unavailableTemplateIds}
          selectedBehaviorId={selectedId}
          behaviorName={doc?.name ?? ""}
          isTemplate={doc?.isTemplate === true}
          onSelectBehavior={actions.selectBehavior}
          onDuplicateBehavior={(id) => actions.duplicateBehavior(id)}
          onRenameBehavior={(id, next) => actions.renameBehavior(id, next)}
          onRenameCurrent={(next) => selectedId && actions.renameBehavior(selectedId, next)}
          onDeleteBehavior={(id) => actions.deleteBehavior(id)}
          onNew={actions.newEmptyBehavior}
          isRunning={isRunning}
          isStartingPhysics={isStartingPhysics}
          canRun={Boolean(selectedId) && !isRunning && !isStartingPhysics}
          onRun={handleRun}
          onStop={handleStop}
          lastEpisodeOutcome={lastEpisodeOutcome}
          lastTickMessage={lastTickMessage}
          activeNodeLabel={activeNodeLabel}
          onCaptureBaseline={() => selectedId && actions.captureBaseline(selectedId)}
          onExport={handleExport}
          onImport={handleImport}
          onDelete={() => selectedId && actions.deleteBehavior(selectedId)}
          canCaptureBaseline={Boolean(selectedId)}
          canExport={Boolean(selectedId)}
          canDelete={Boolean(selectedId) && !doc?.isTemplate}
        />
        {userHint ? (
          <div className="border-b border-amber-900/60 bg-amber-950/25 px-3 py-1.5 text-[11px] text-amber-200">
            {userHint}
          </div>
        ) : null}
        {importError ? (
          <div className="border-b border-rose-900/60 bg-rose-950/30 px-3 py-2 text-[11px] text-rose-200">
            {importError}
          </div>
        ) : null}
        {runValidationIssues.length > 0 ? (
          <div className="border-b border-rose-900/60 bg-rose-950/30 px-3 py-2 text-[11px] text-rose-200">
            <div className="font-semibold">Resolve behavior references before running:</div>
            <div className="mt-1 space-y-0.5">
              {runValidationIssues.map((issue) => (
                <div key={issue}>{issue}</div>
              ))}
            </div>
          </div>
        ) : null}

        <div
          className="relative flex-1 min-h-0 min-w-0"
          style={{ backgroundColor: "rgba(28, 28, 30, 0.98)" }}
        >
          {doc ? (
            <BehaviorTreeCanvas
              doc={doc}
              onDocChange={actions.updateDocument}
              bodies={bodies}
              activeNodeId={activeNodeId}
              nodeStatuses={nodeStatuses}
            />
          ) : (
            <EmptyCanvas hasBehaviors={behaviorList.length > 0} onNew={actions.newEmptyBehavior} />
          )}
        </div>
      </div>
    </DraggablePanel>
  );
}
